import axios from "axios";
import { jwtDecode } from "jwt-decode";
import {
  AUTH,
  StorageKeys,
  globalConstants,
  responseStatus,
  serverResponseConstants,
  viewPathConstants,
} from "../constants";
import { getLocalStorageItem, setLocalStorageItem } from "./localStorage";
import { clearUserData } from "./utility-functions";
import store from "../redux/store";
import { logout } from "../redux/actions/auth.action";

export const refreshToken = (token) => {
  return axios.post(
    process.env.REACT_APP_BASE_API_URL +
      `/${globalConstants.VERSION}/${AUTH}/ResendToken?token=${token}`
  );
};

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_BASE_API_URL,
});

let isRefreshing = false;
let refreshSubscribers = [];

const subscribeTokenRefresh = (cb) => refreshSubscribers.push(cb);

const onTokenRefreshed = (token) => {
  refreshSubscribers.map((cb) => cb(token));
  refreshSubscribers = [];
};

const isTokenExpired = (token) => {
  if (!token) return true;
  const decodedToken = jwtDecode(token);
  return decodedToken.exp * 1000 < Date.now();
};

axiosInstance.interceptors.request.use(
  async (config) => {
    const token = getLocalStorageItem(StorageKeys.Token)?.token;
    const keepMeSignedIn = getLocalStorageItem(StorageKeys.KeepMeSignedIn);
    if (keepMeSignedIn && token && isTokenExpired(token)) {
      if (!isRefreshing) {
        isRefreshing = true;
        try {
          const response = await refreshToken(token);
          const newToken = response.data.message;
          setLocalStorageItem(StorageKeys.Token, { token: newToken });
          onTokenRefreshed(newToken);
        } catch (error) {
          clearUserData();
          store.dispatch(logout());
          window.history.replaceState(
            null,
            null,
            viewPathConstants.DEFAULT_PATH
          );
        } finally {
          isRefreshing = false;
        }
      }

      const retryOriginalRequest = new Promise((resolve) => {
        subscribeTokenRefresh((token) => {
          config.headers["ssf-auth"] = "Bearer " + token;
          resolve(config);
        });
      });

      return retryOriginalRequest;
    }

    if (!config.headers["ssf-auth"] && token) {
      config.headers["ssf-auth"] = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (
      error.response &&
      error.response.status === responseStatus.UNAUTHORIZED
    ) {
      clearUserData();
      store.dispatch(logout());
      window.history.pushState(null, null, viewPathConstants.DEFAULT_PATH);
    }
    return Promise.reject(error);
  }
);

export default axiosInstance;
